import { Injectable } from '@angular/core';
import { OrigamiHttpResponse, OrigamiResponse, ServiceBase } from './base/service.base';
import { Observable, of } from 'rxjs';

import {
  DeviceInfo,
  Folder,
  SearchDataType,
  SimpleTopic,
  TIAutomateEntry,
  Topic,
  TopicACL,
  TopicField,
  TopicUtils,
} from '../utils/topic-utils';
import { ErrorCode } from '@app/origami-api/constants/error-codes';
import { ConfigServiceModel } from '@app/origami-api/services/config.service';
import { WorkflowStageApprovalType } from '../constants/workflow';
import { tap } from 'rxjs/operators';
import { FileProcessingType } from '@app/origami-api/constants/file-processing';
import { ActionOnDuplicate } from '../constants/document-action-duplicate';
import { DocumentDuplicateStatus } from '@app/origami-api/constants/document-duplicate-status';
import { DocumentDistributionMode } from '@app/origami-api/constants/document-distribution-mode';
import { DocumentDistributionDestinationModule } from '@app/origami-api/constants/document-distribution-destination-module';
import { AdditionalRequestOptions } from '@app/origami-api/services/base/request-proxy/request-proxy.class';
import { RoomTypes } from '@app/origami-api/constants/room-types';
import { tiStringsFormat } from '@app/shared/pipes/string-format.pipe';
import { GLTrainingStatus } from '@app/origami-api/constants/gl-training-status';
import { TIAutomateTaskType } from '@app/origami-api/constants/ti-automate-task-type';
import { AgencySubmissionModels } from '@app/pages/main/pages/startup-agency-submission/models/agency-submission.models';
import { HttpXhrBackend } from '@angular/common/http';
import { CASAuthRequestProxy } from './base/request-proxy/cas-auth.request-proxy.class';
import { FieldNames, OrigamiModuleType } from '../constants';
import { Converter } from '@app/shared/utils/converter';
import { TimeZoneService } from '@app/services/time-zone.service';
import { SignStatus } from '../constants/eSignatureStatus-type';
import { DateFormatStore } from '@app/session/date-format-store';
import { StudyStartupServiceModels } from '@app/origami-api/services/study-startup.service';
import { SessionService } from '@app/session/session.service';
import { StorageTypeId } from '@app/origami-api/constants/storage-type';
import { SSUEssentialDocument } from '@app/origami-api/services/essential-documents.service';
import TIAutomateDocumentData = FolderManagementServiceModel.TIAutomateDocumentData;
import StartUpDocumentReplaceOptions = SSUEssentialDocument.StartUpDocumentReplaceOptions;
import { TeamGroupType } from '@app/origami-api/services/teams.service';
import { WorkflowStatusType } from '@app/origami-api/constants/workflow-status-type';
import { TopicServiceOptions } from '@app/origami-api/models/topic-service.model';
import { UserActivityType } from '@app/origami-api/constants/user-activity-type';

export function getVersionAdditionalText(version: FolderManagementServiceModel.DocumentVersion, stringsSource: any) {
  const separator = stringsSource.versionTextSeparator;
  return version.IsPromotedVersion && version.PreviousLabel
    ? separator + tiStringsFormat(stringsSource.promotedFromPattern, version.PreviousLabel)
    : version.IsConvertedVersion
      ? separator + stringsSource.pdf
      : '';
}

export namespace FolderManagementServiceOptions {
  import SSUReviewerApprovalMode = StudyStartupServiceModels.SSUReviewerApprovalMode;
  export const DELETED_VIEW_ALL_FOLDER_ID = -1;

  export enum FolderTreeViewModes {
    BY_INDEX = 0,
    BY_GROUP = 1,
    BY_ROLE = 2,
    BY_SECURITY = 3,
    BY_DATE = 4,
    DELETED_BY_USER = 5,
    MY_SUBMISSIONS = 6,
    MY_REVIEWS = 7,
    BY_WORKFLOW = 8,
    PROCESSED = 10,
    UNCLAIMED = 11,
    BY_DOCUMENT_TYPE = 12,
    BY_INVESTIGATIVE_SITE = 13, // we do not have a folder view for that - using a separate IS view
    BY_COUNTRY = 26,
    // Audit views
    AUDIT_BY_INDEX = 1000,
    AUDIT_BY_DOCUMENT_TYPE = 1001,
    AUDIT_BY_COUNTRY = 1002,
    AUDIT_BY_INVESTIGATIVE_SITE = 1003,
    MY_AUDIT_PENDING = 28,
    MY_AUDIT_AUDITED = 29,
    MY_AUDIT_IN_PROGRESS = 33,
    MY_AUDIT_RESOLVED = 36,
    BY_AUDITOR_PENDING = 30,
    BY_AUDITOR_AUDITED = 31,
    BY_AUDITOR_IN_PROGRESS = 34,
    BY_AUDITOR_RESOLVED = 37,
    AUDIT_TO_FIX = 35,
    PARTICULAR_AUDITORS_FOR_EXPORT = 38,
    AUDIT_FINDINGS = 50,
    AUDIT_QUERY = 51,
    AUDIT_QUERY_BY_RECIPIENTS = 56,
    MATCHING_DOCUMENTS = 57,
    WORKING_AREA = 59,
    BY_TAG = 60,
    OBSOLETE_DOCUMENTS = 64,
    BY_RISK_SCORE = 87,
    BY_EMAIL_DOCUMENTS = 89,
    SSU_RR_BY_REG_APPROVE_STATUS = 15,
    SSU_RR_BY_SITE = 16,
    SSU_RR_BY_COUNTRY = 17,
    SSU_RR_BY_IRB = 18,
    SSU_RR_BY_DOCUMENT_TYPE = 19,

    SSU_BY_SITE = 20,
    SSU_BY_COUNTRY = 21,
    SSU_BY_DOCUMENT_TYPE = 22,
    SSU_BY_FROM_INBOX = 23,
    SSU_BY_IRB = 43,
    BY_REVIEWER = 25,
    BY_DOCTYPE_TEMPLATED_INDEX = 27,

    SSU_BY_DATE = 32,
    MODIFY_INDEX = 39,
    RETURN_ALL_FOLDERS = 40,

    QUERY_RESOLUTION = 41,
    QUERY_BY_RECIPIENTS = 55,

    SIGN_DOCS = 42,

    REJECTION_DOCS = 44,
    AGE_QUERY = 45,

    VIEW_MODE_BY_STATUS_IGNORE_ACL = -8,

    VIEW_MODE_ROOM_IGNORE_ACL = -24,

    PROGRESS_VS_UNCLAIMED = -46,
    MY_SUBMISSIONS_DASH = -47,
    MY_QUERIES_DASH = -48,

    ETMF_COMPLETENESS = 49,

    WORKFLOW_BY_STAGE = 52,
    DOCUMENT_LIFECYCLE = 58,
    CHANGE_CONTROL_WORKFLOW_DOCUMENTS = 62,

    WORKING_DOCUMENTS = 63,
    RESPONSIBLE_DEPARTMENT = 65,
    MY_PLANNED = -65,
    EVENT = -66,
    EVENT_DOCUMENTS = 66,
    RECENT_DOCUMENTS = 67,
    FAVOURITE_DOCUMENTS = 68,
    REDACTED_DOCUMENTS = 69,

    //CTMS related
    CTMS_MY_REPORTS = 70,
    CTMS_MY_REVIEWS = 71,
    CTMS_SITE_VISIT_DOCUMENTS = 72,

    BY_CONFIDENCE_SCORE = 73,
    BY_CONFIDENCE_SCORE_DISCREPANCY = 74,
    CTMS_REPORTS_TO_REGENERATE = 75,

    RA_BY_STATUS = 76,
    RA_BY_RECIPIENT = 79,
    MY_RA_SENT = 80,
    MY_RA_RECEIVED = 81,

    MY_EDITS = 77,
    EDITS_IN_PROGRESS = 78,

    // SSU Sponsor Review
    SPONSOR_ALL = 82,
    SPONSOR_BY_INVESTIGATIVE_SITE = 83,
    SPONSOR_BY_COUNTRY = 84,
    SPONSOR_BY_IRB = 85,
    SPONSOR_BY_DOCUMENT_TYPE = 86,

    BY_INDEX_READ = 100,

    DOCUMENT_SECURITY = 9000, // Used only for UI

    CHECKOUT_BY_ME = 9001, // Used only for UI
    ///
    CHECKOUT_BY_ANYONE = 9010, // Used only for UI
    TEAM_EDIT = 9011, // Used only for UI
    ///
    MY_DOCUMENTS = 9002, // Used only for UI
    MY_RECENTS = 9003, // Used only for UI
  }

  export enum MergeDocumentsSaveMode {
    Download = 1,
    SaveAsNew = 2,
  }

  export enum FoldersOrderByMode {
    noSorting = 0,
    byIndexPath = 1,
    bySortOrderPath,
  }

  export enum DocumentSubmissionStatus {
    submitted = 1,
    published = 2,
    rejected = 3,
    publishedToEtmf = 4,
  }

  export enum RevertType {
    regular = 1,
    failedOcr = 2,
  }

  export enum TrainingStatus {
    CanNotBeSent = 0,
    Sent = 1,
    ReadyForSending = 2,
    WorkflowApprovalRequired = 3,
    PDFConversionRequired = 4,
  }

  export interface GetFolderIndexTreeFields {
    siteId: number;
    viewMode: FolderTreeViewModes;
    parentId?: number;
    foldersIds?: number[];
    filterValue?: string | number;
    foldersFilter?: number;
    folderSearchQuery?: string;
    docSearchQuery?: string;
    orderBy?: number;
  }

  export interface GetDocumentsByViewParams {
    siteId: number;
    documentId?: number;
    parentId?: number | null;
    viewMode?: number | SubmissionProfileViewMode;
    first?: number;
    limit?: number;
    unreadOnly?: boolean;
    craUserIds?: number[] | null;
    search?: string | number | null;
    fieldListing?: string | string[];
    searchQuery?: string;
    orderByClause?: string | null;
    ftsQuery?: string;
    ftsQueryDataType?: SearchDataType;
    searchByFieldsInIndex?: boolean;
    newData?: any;

    viewFilter?: string;
    start?: number;
    searchClause?: string;

    expand?: boolean;
    skipRequest?: boolean;
    approvalMode?: SSUReviewerApprovalMode;
  }

  export enum SubmissionProfileViewMode {
    ByCountry = 1,
    BySubmissionProfileIds = 2,
    ByStatus = 3,
  }

  export enum EntityMatchingOptions {
    DoNotMatch = 0,
    MatchInvestigativeSite = 1,
    MatchContact = 2,
    MatchInstitutionalReviewBoard = 4,
  }

  export interface EnqueueFileParams {
    siteId: number;
    topicIds: number[];
    fileProcessingTypeId: FileProcessingType;
    fileProcessingOptions?: {
      OcrOptions?: {
        Languages?: string[];
      };
      OptimizationOptions?: {
        FixFileBeforeOptimization?: boolean;
      };
      PublishReadyForETMFDocumentsOptions?: {
        ErrorCodes?: number[];
        LinkedRoomIds?: number[];
      };
    };
  }

  export interface DeleteDocumentParams {
    siteId: number;
    documents: {
      ChildTopicId: number;
      ParentTopicId: number;
      TopicTypeId?: number;
    }[];
    removalReason: string;
  }

  export interface ICopyDocuments {
    sourceSiteId: number;
    targetSiteIds: number[] | string[];
    documentIds: number[];
    publishAsFinalDocuments: boolean;
    filterType: number;
    mode?: DocumentDistributionMode;
    prepareErrorReport?: boolean;
    fileAttachmentType?: number;
    destModule?: RoomTypes;
  }

  export interface ModifyIndexAndUnlock {
    siteId: number;
    rootId: number;
    actions: object[];
  }

  export interface GetDocument {
    siteId: number;
    topicId: number;
    getDocumentParams?: {
      IncludeTopicAcls?: boolean;
      IncludeWorkflow?: boolean;
      IncludeAudit?: boolean;
      IncludeQueryTickets?: boolean;
      IncludeSuggestedDocumentTypes?: boolean; // should not be used, we have a separate API for that. it drastically increases the API execution time
      QueryTicketCreatedBy?: string;
      QueryEntityId?: number;
      ExcludeCustomForm?: boolean;
      QueryType?: number;
      IncludePostedDeviceInfo?: boolean;
      FieldListing?: string; //string | string[];?
    };
    tmfPublishing?: {
      RetrieveFromTmf: boolean;
    };
  }

  export interface GetWorkingDocumentId {
    siteId: number;
    documentId: number;
  }

  export interface GetDocumentCollision {
    document: FolderManagementServiceModel.Document;
    siteId: number;
  }

  export interface GetDocumentVersions {
    siteId: number;
    topicId: number;
  }

  export interface CopyDocument {
    copyMode: number;
    documentList: number[];
    siteId: number;
    sourceFolder?: number;
    targetFolder?: number;
    updateReason?: string;
  }

  export interface BulkImportFromMetadataImportOptions {
    BulkImportMode: number;
    FilePath: string;
    BulkImportWorkSheetOptions: any[]; // any? any suggestions?
    SkipFinalDocuments?: boolean;
    Reason?: string;
  }

  export interface BulkImportItemsFromMetadata {
    siteId: number;
    importOptions: BulkImportFromMetadataImportOptions;
  }

  export interface GetDocumentOnlyOfficeInfo {
    siteId: number;
    topicId: number;
  }

  export interface GetDocumentSharepointInfo {
    siteId: number;
    topicId: number;
  }

  export interface GetDocumentsByDocumentTypeFilter {
    docTypeFilterFieldName: string;
    docTypeFilterValue: number;
    siteId: number;
  }

  export interface GetFolderProfile {
    siteId: number;
    folderId: number;
    childTopicId?: number;
  }

  export interface CreateDocument {
    siteId: number;
    document: FolderManagementServiceModel.Document;
    /*    userName?: string;
    userPassword?: string;
    validationTransactionId?: string;
    validationType?: UserValidationType;

    UserName?: string;
    UserPassword?: string;
    TransactionId?: string;
    UserIdentityValidationType?: UserValidationType;*/
  }

  export type EditDocument = CreateDocument;

  export interface RevertDocument {
    description: string;
    documentId: number;
    siteId: number;
    version?: string;
    revertType: FolderManagementServiceOptions.RevertType;
  }

  export interface GetDocumentProfile {
    siteId: number;
    topicId: number;
    tmfPublishing?: {
      RetrieveFromTmf: boolean;
    };
  }

  export interface MergeDocuments {
    siteId: number;
    mergeDocumentsOptions: {
      SaveMode: FolderManagementServiceOptions.MergeDocumentsSaveMode;
      PrintTopicsParams: any;
    };
  }

  export interface MakeDocumentsObsolete {
    siteId: number;
    topicIds: number[];
    comments: string;
  }

  export interface SaveIgnoredSuggestedDocumentTypes {
    siteId: number;
    documentId: number;
    chosenDocTypeId: number;
    ignoredDocTypes: {
      DocumentTypeId: number;
      Score: number;
    }[];
  }

  export interface GetDocumentSubmissionInfo {
    siteId: number;
    statusIds: DocumentSubmissionStatus[];
    billingDay?: number;
    countries?: string[];
  }

  export interface SetDuplicateStatus {
    siteId: number;
    duplicateStatusId: DocumentDuplicateStatus.Duplicate | DocumentDuplicateStatus.NotDuplicate;
    topicIds: number[];
  }

  export interface SetDocumentStartingVersionParams {
    siteId: number;
    documentTopicId: number;
    versionLabel: string;
  }

  export interface GetDocumentDistributionLogParams {
    sourceDocumentId?: number;
    targetDocumentId?: number;
    mode: 0 | 1; // 0: Common; 1: Latest records grouped by target room
  }

  export interface UpdateNeedRedaction {
    siteId: number;
    documents: {
      TopicId: number;
      NeedRedaction: string;
    }[];
  }

  export interface SendDocumentsToTrainingParams {
    siteId: number;
    topicIds: number[];
  }

  export interface RejectDocumentsParams {
    siteId: number;
    topicIds: number[];
    reasonTopicIds: number[];
    comments: string;
  }

  export interface GetDocumentConfidenceLevelScoreParams {
    siteId: number;
    documentId: number;
  }

  export interface GetTIAutomateEntriesParams {
    siteId: number;
    documentId: number;
    matchingOptions: EntityMatchingOptions;
  }

  export interface GetTiAutomateTaskDataParams {
    siteId: number;
    documentId: number;
  }

  export interface SubmitTIAutomateTaskParams {
    siteId: number;
    topicId: number;
    reuseExistingData: boolean;
    enforceExtractDataIfNotExists: boolean;
    taskTypeId: TIAutomateTaskType;
  }

  export interface MoveDocumentsBetweenModulesOpt {
    siteId: number;
    moveDocumentData: {
      SourceModule: OrigamiModuleType;
      TargetModule: OrigamiModuleType;
      TargetFolder?: number;
      DocumentTopics: Topic[];
      DocReplaceOption?: StartUpDocumentReplaceOptions;
      MoveAsFinal?: boolean;
    };
  }

  export interface SetAiFlagStatusParams {
    siteId: number;
    aiFlagId: number;
    documentId: number;
    aiFlagStatus: number;
    reason?: string;
  }

  export interface GetAiFlagDetailsParams {
    siteId: number;
    documentId: number;
    aiFlags?: number[];
  }
}

export namespace FolderManagementServiceModel {
  export interface AiFlagActionResult {
    AiFlagId: number;
    DocumentId: number;
    NewStatusId: number;
  }

  export interface AiFlagDetail {
    AiFlag: import('./config.service').AiFlag;
    AiFlagSeverityId: number;
    AiFlagStatusId: number;
    CheckIds: number[];
  }
  import TopicWorkflowStageData = TopicServiceOptions.TopicWorkflowStageData;
  import AccessRightInfo = TopicServiceOptions.AccessRightInfo;
  import SubmissionDocumentSourceType = AgencySubmissionModels.SubmissionDocumentSourceType;

  export interface DocumentProfile {
    CreatedByName: string;
    CreatedDateTime?: string;
    DocumentIndexList: {
      CopyNum: number;
      IndexPath: string;
      IndexPathIds: string; // number?
      TopicId: number;
    }[];
    FileName: string;
    FileSize: number;
    LastUpdatedByName: string;
    LastUpdatedDateTime?: string;
    Topic: SimpleTopic;
    TopicACLs: TopicACL[];
    InheritFolderSecurity?: boolean;
  }

  export interface WorkflowCriteria {
    DtSearchClause: string;
    FilterValue: string;
    Name: string;
    SearchQuery: string;
    Trigger: number;
  }

  export interface WorkflowDocument {
    CurrentStageId: number;
    LastStageId: number;
    Status: number;
    TopicId: number;
  }

  export interface WorkflowStageApproverData {
    ApproverId: number;
    ApproverName: string;
    ApproverRoleId: number;
    ApproverUserName: string;
    ClaimStatus: number;
    ClaimedBy: number;
    Comments: string;
    IndexPosition: string;
    IsApprover: boolean;
    IsCurrent: boolean;
    IsFinalStage: boolean;
    IsLastStage: boolean;
    Issues: string;
    LastUpdated: string;
    StatusId: number;
    StatusName: string;
    StatusTypeId: number;
    StatusTypeName: string;
  }

  export interface WorkflowStageStatus {
    ActionName: string;
    ActionColorId: number;
    ActionTriggerIds: number[];
    DocumentStatusTopicId: number;
    IsRestricted: boolean;
    Name: string;
    StatusTypeId: WorkflowStatusType;
    WorkflowStatusId: number;
    WorkflowStatusTopicId: number;
  }

  export interface InitialStageConditions {
    Criteria: WorkflowCriteria;
    InitialStage: number;
    InitialStageConditionId: number;
  }

  export interface WorkflowStageForm {
    FormType: number;
    DisplayName: string;
    AssignedForStage: boolean;
    AssignedForStageId: number;
    AccessRight: number;
    StageFormId?: number;
    StageFormCustomFields?: ConfigServiceModel.Form;
  }

  export interface EventRecipient {
    AuthorityTypes: TeamGroupType[];
    GroupIds: number[];
    UserNames: string[];
  }

  export interface RecordType {
    RecordTypeId: number;
    RecordTypeName?: string;
    RecordTypeCategoryId?: number;
  }

  export interface WorkflowEvent {
    Enabled: boolean;
    EventId: number;
    Name: string;
    NotifyAssignees: boolean;
    NotifyOwner: boolean;
    Recipients: EventRecipient;
    Type: number;
  }

  export interface WorkflowStageActionField {
    FieldName: string;
    FieldType: number;
    Fieldvalue: any;
  }

  export interface WorkflowStageAction {
    Name: string;
    Criteria: Omit<WorkflowCriteria, 'Trigger'>;
    IsFinal: boolean;
    Index: number;
    JumpTo: number;
    Fields: WorkflowStageActionField[];
  }

  export enum WorkflowStageContributorStatus {
    Pending,
    InProgress,
    Completed,
    Rejected,
  }

  export enum WorkflowStageContributorGroupStatus {
    Active = 1,
    Inactive = 2,
  }

  export interface WorkflowStageContributorHistory {
    Comments: string | null;
    CreatedDate: string | null;
    LogId: number | null;
    StatusId: WorkflowStageContributorStatus;
    ContributorFullName: string;
    ContributorName: string;
    ContributorUserName: string;
    ContributorEmailAddress: string;
    ContributorId: number;
    WorkflowStageId: number;
    ContributorStatusId: WorkflowStageContributorGroupStatus;
  }

  export interface WorkflowStage {
    ParentStage?: number;
    Actions: WorkflowStageAction[];
    ApproverData: WorkflowStageApproverData;
    ApproverGroupId: number;
    Approvers: { GroupIds: number[]; GroupNames: string[]; UserNames?: string[] };
    AssignOwner: boolean;
    ContributorGroupId?: number;
    ContributorHistory?: WorkflowStageContributorHistory[];
    AuthorityType: TeamGroupType;
    AutoRelease: number;
    AutoReleaseRange: string;
    CommentOnlyReview?: boolean;
    DueDays: number;
    DueDaysRange: string;
    EscalationFrequency: number;
    EscalationFrequencyRange: string;
    Events: WorkflowEvent[]; // todo
    Fields: string[] | null;
    Forms: WorkflowStageForm[];
    IsDocumentOwnerStageApprover?: boolean;
    Issues: string[];
    IssuesNames: string[];
    MinimumNumberOfApprovers: number;
    RequireEvidences: boolean;
    RequiredRecordTypes: RecordType[];
    Reminder: number[];
    ReminderRange: string;
    RequireEsignature: boolean;
    SendApproveRejectNotificationToDocumentOwner?: boolean;
    SendNotificationToDocumentOwner?: boolean;
    StageApprovalType: WorkflowStageApprovalType;
    StageId: number;
    StageName: string;
    StageNumber: number;
    StageProcessingType: number;
    Statuses: WorkflowStageStatus[];
  }

  export interface Workflow {
    CreatedBy: number;
    CreatedByUserName: string;
    CreatedDate: string;
    LastUpdatedDate: string;
    LastUpdatedBy: string;
    LastUpdatedByUserName: string;
    Criteria: WorkflowCriteria;
    Description: string;
    Document: WorkflowDocument;
    RecordTypes: RecordType[];
    EnableLmsIntegration: boolean;
    FormType: number;
    History: WorkflowHistory[];
    InitialStageConditions: InitialStageConditions[];
    Name: string;
    Scope: number;
    Stages: WorkflowStage[];
    Status: number;
    TopicId: number;
    TopicTypeId: number;
    Type: number;
  }

  export interface WorkflowHistory {
    Comments: string;
    IndexPosition: string;
    Issues: string[];
    ReviewDate: string;
    ReviewerFullName: string;
    ReviewerUserId: number;
    StageId: number;
    StageName: string;
    StatusId: number;
    StatusTopicId: number;
    StatusName: string;
    StatusTypeId: number;
    UserActivity: string;
    UserActivityTypeId: UserActivityType;
    WorkflowId: number;
    WorkflowLogId: number;
    WorkflowName: string;
    WorkflowStageLogId: number;
    WorkflowStatusId: number;
    WorkflowTopicTypeId: number;
  }

  export interface IDocumentESignature {
    CanStartSigning: boolean;
    Comments: string;
    Id: number;
    Order: number;
    Reason: string;
    SignerRole: string;
    SignatureDate: string;
    SignerEmailAddress: string;
    SignerDisplayName: string;
    SignerName: string;
    Status: number;
    TopicFields: any;
  }

  export interface IESignatureStatus {
    SignStatus: SignStatus;
    SignatureDate: string;
    UserDisplayName: string;
    UserName: string;
  }

  export interface IDocumentESignatureRequest {
    CreatedById: number;
    EnvelopeId: string;
    ExternalId: number;
    Id: number;
    Name: string;
    ProviderType: number;
    Signatures: IDocumentESignature[];
    StatusId: number;
    Type: number;
  }

  export interface IAuditHistory {
    AuditInformationTopicId: number;
    AuditProfileId: number;
    AuditProfileName: string;
    AuditStatusTopicId: number;
    AuditStatusTypeId: number;
    AuditorUserId: number;
    AuditorUserName: string;
    Comments: string;
    ContainsPhi: boolean;
    CreatedBy: number;
    CreatedByUserName: string;
    CreatedDate: string;
    CreatedDateConverted: string;
    DocumentAuditInformationId: number;
    DocumentTopicId: number;
    Metadata: TopicField[];
    AuditProfileStatusId?: number;
  }

  export interface QueryTicketActivityRecipient {
    EmailAddress: string;
    FullName: string;
    QueryTicketActivityId?: number;
    RecipientId?: number;
    UserId?: number;
    RoleLevel?: number; //RoleLevel
  }

  export interface QueryTicketActivity {
    Comments: string;
    CreatedBy: number;
    CreatedByUserName: string;
    CreatedByRoleLevel?: number;
    CreatedDate: string;
    CreatedDateString?: string; // optional?
    CreatedUserName: string;
    Issues: string;
    QueryTicketActivityFiles: any[];
    QueryTicketActivityId: number;
    QueryTicketActivityReminders: any[];
    QueryTicketActivityTypeId: number;
    QueryTicketId: number;
    Recipients: string;
    QueryTicketActivityRecipients?: QueryTicketActivityRecipient[];
    Title: string;
    TrueCopyCertificate: string;
    isFirst?: boolean; // virtual
  }

  export interface QueryTicket {
    Comments: string;
    CreatedBy: number;
    CreatedByUserName: string;
    CreatedDate: string;
    CreatedDateString?: string;
    CreatedUserName: string;
    LastUpdatedBy: number;
    LastUpdatedByUserName: string;
    LastUpdatedDate: string;
    LastUpdatedDateString: string;
    LastUpdatedUserName: string;
    QueryEntityId: number;
    QueryTicketActivities: QueryTicketActivity[];
    QueryTicketId: number;
    QueryTypeId: number;
    RelatedEntityId: number;
    RelatedEntityTypeId: number;
    SiteId: number;
    StatusId: number;
    Title: string;
  }

  export interface Document extends SimpleTopic {
    DuplicatesResolveMode?: ActionOnDuplicate;
    AuditHistory?: IAuditHistory[];
    CreatedBy?: number;
    CreatedDate?: string;
    QueryTickets?: QueryTicket[];
    CustomForm?: ConfigServiceModel.Form;
    ESignatureRequest?: IDocumentESignatureRequest;
    LockedBy?: number;
    ParentId?: number;
    SiteId?: number;
    SuggestedDocTypes?: any[];
    TopicFields?: TopicField[];
    TopicId?: number;
    TopicName: string;
    TopicTypeId?: number;
    WorkflowHistory?: WorkflowHistory[];
    ReviewDocumentWFHistory?: WorkflowHistory[];
    Workflows?: Workflow[];
    IsTitleChanged?: boolean;
    ModifiedDefaultFields?: string[];
    FileToken?: string;
    DocumentUrl?: string;
    SourceTopicId?: number;
    SourceTopicFileId?: number;
    ACL?: string; // deprecated, use AclList instead
    AclList?: AccessRightInfo[];
    ACLs?: TopicACL[];
    InheritFolderSecurity?: boolean;
    TopicWorkflowStageFields?: TopicWorkflowStageData[];
    ReplaceAttachmentOnly?: boolean;
    ReplaceAttachmentReason?: boolean;
    PostedDeviceInfo?: DeviceInfo;
    ReadyForWorkflow?: boolean;
    IsEffectiveDocument?: boolean;
    DocumentFileName?: string;
    SortOrder?: number;
    Source?: SubmissionDocumentSourceType;

    DocReplaceOption?: number;
    FileName?: string;
    IsDocumentCollected?: boolean;
    SSUDocumentTopicId?: number;
    SSUMetaDataFields?: TopicField[];
    StartupHistory?: StartupHistory[];
  }

  export interface StartupHistory {
    CreatedBy: string;
    RecordDate: string;
    EventType: number;
    QCReviewStatus?: number;
    RegulatoryReviewStatus?: number;
    SponsorReviewStatus?: number;
  }

  export interface DocumentView {
    DocCount?: number;
    PlaceholdersCount?: number;
    FolderCount?: number;
    FolderId: number;
    FolderName: string;
    FolderPrefix?: string;
    ParentId?: number;
    SortOrder?: number;
    TopicTypeId?: number;
    TotalDocCount?: number;
    TotalPlaceholdersCount?: number;
    ReadOnly?: boolean;
    CraUserIds?: string;
    IsUsedInDocTypes?: boolean;
    IsUsedInWorkflow?: boolean;
    CanBeDeleted?: boolean;
    AccessRight?: number;
    TemplateFolderId?: number;
    InvestigativeSiteId?: number;
    CountryCode?: string;
    AclHash?: string;
  }

  export interface DocumentCollisionInfo {
    DuplicateDocumentFileTypes: null; // TODO: what type?
    DuplicateDocumentId: number;
    ErrorCode: number;
    HasEditRight: boolean;
    IsDuplicatesExist: boolean;
    ParentId: number;
    ParentName: null;
    TopicId: number;
    TopicName: string;
  }

  export interface GetDocumentByViewResult {
    TopicList: Topic[];
    PageNumber?: number;
    TotalRowCount: number;
    errCodeAddTopicsToCollection?: number[];
    errCodeCreateCollection?: any[];
  }

  export interface FolderProfile {
    CreatedByName: string;
    // CreatedDateTime: string; // not used?
    LastUpdatedByName: string;
    // LastUpdatedByDateTime: string; // wrong name, can not be used
    FolderIndexPath: {
      FolderId: number;
      IdPath: string;
      IndexPath: string;
      Prefix: string;
      TopicTypeId: number;
    };
    Topic: Topic;
    TopicACLs: TopicACL[];
  }

  export interface DocumentVersion {
    CreateDate: string;
    CheckInDate: string;
    Description: string;
    DocumentUrl: string;
    FullName: string;
    HasContent: boolean;
    Id: string;
    Label: string;
    Name: string;
    UserName: string;
    IsDraftVersion: boolean;
    IsApprovedVersion: boolean;
    IsEffectiveVersion?: boolean; // concept?
    IsPromotedVersion?: boolean;
    IsConvertedVersion?: boolean;
    IsRejectedVersion?: boolean; //not yet implemented
    TrainingNeeded: boolean;
    TrainingStatusId?: GLTrainingStatus;
    PreviousLabel?: string;
    StorageType?: StorageTypeId;
  }

  export interface ReplaceAttachmentURL {
    DocumentDate: string;
    DuplicateDocumentId: number;
    ErrorCode: number;
    ErrorMessage: string;
    ErrorRecords: {
      ErrorCode: number;
      ObjectName: string;
      ObjectType: string;
    }[];
    FieldId: number;
    InvalidFolders: any[]; //TODO:
    TopicId: number;
    TopicName: string;
    UploadedFileName: string;
  }

  export interface CopyDocument {
    DocumentDate: string;
    DuplicateDocumentId: number;
    ErrorCode: number;
    ErrorMessage: string;
    ErrorRecords: string;
    FieldId: number;
    InvalidFolders: string[];
    TopicId: number;
    TopicName: string;
    UploadedFileName: string;
  }

  export interface DocumentDistributionLog {
    CreatedBy: number;
    CreatedByName: string;
    CreatedDate: string; // CSharpDate
    DestinationModule: DocumentDistributionDestinationModule;
    DistributionTypeId: DocumentDistributionMode;
    Id: number;
    SourceDocumentId: number;
    SourceRoomId: number;
    SourceFolderId: number;
    SourceFolderIdPath: string;
    SourceRoomName: string;
    TargetDocumentId: number;
    TargetFolderId: number;
    TargetFolderIdPath: string;
    TargetRoomId: number;
    TargetRoomName: string;
    UserActivityLogId: number;
  }

  export interface OnlyOfficeInfo {
    AbovePreviewThreshold: boolean;
    DocumentUpdatedCallbackUrl?: string;
    CallbackUrl: string;
    DocumentTitle: string;
    DocumentUrl: string;
    DocumentName: string;
    Key: string;
    LockOwnerId: number;
    LockOwnerName: string;
    MimeType: string;
    Mode: string;
    OnlyOfficeUrl: string;
    Status: number;
    Timeout: number;
  }

  export interface SharepointInfo {
    Path: string;
    LockOwnerName?: string;
    LockOwnerId?: number;
  }

  export interface GetTrainingStatus {
    ErrorCode: number;
    TopicId: number;
    TrainingStatus: FolderManagementServiceOptions.TrainingStatus;
    Version: number;
  }

  export interface TIAutomateDocumentData {
    DocumentScore: number;
    Entries: TIAutomateEntry[];
    Summary: string | null;
  }
}

@Injectable({
  providedIn: 'root',
})
export class FolderManagementService extends ServiceBase {
  serviceUrl = 'FolderManagmentService.svc';

  constructor(
    backend: HttpXhrBackend,
    requestProxy: CASAuthRequestProxy,
    private timeZoneService: TimeZoneService,
    private sessionService: SessionService,
  ) {
    super(backend, requestProxy);
  }

  getDocumentIndexPosition(
    body: {
      siteId: number;
      autoIndexOnly: boolean;
      documentData: { TopicId?: number; TopicName?: string; TopicFields: TopicField[] };
    },
    forceRequest?,
  ): Observable<OrigamiResponse<Folder>> {
    return this.origamiPost('/GetDocumentIndexPosition', body, null, [ErrorCode.AUTO_INDEXING_ERROR], forceRequest);
  }

  getDocumentProfile(
    body: FolderManagementServiceOptions.GetDocumentProfile,
    forceRequest: boolean = true,
  ): Observable<OrigamiResponse<FolderManagementServiceModel.DocumentProfile>> {
    return this.origamiPost('/GetDocumentProfile', body, null, null, forceRequest).pipe(
      tap((res) => {
        if (!res.Result) {
          return;
        }
        const result: FolderManagementServiceModel.DocumentProfile = res.Result;
        const createdDate = TopicUtils.getTopicFieldValue(result.Topic.TopicFields, FieldNames.Document.CreatedDate);
        if (createdDate) {
          const date = Converter.DateAndTime.parseStringToJSDate(
            createdDate,
            this.sessionService.domain?.timeZone.IanaZoneName,
          );
          result.CreatedDateTime = this.timeZoneService.formatDate(date);
        }
        const lastUpdatedDate = TopicUtils.getTopicFieldValue(
          result.Topic.TopicFields,
          FieldNames.Document.LastUpdatedDate,
        );
        if (lastUpdatedDate) {
          const date = Converter.DateAndTime.parseStringToJSDate(
            lastUpdatedDate,
            this.sessionService.domain?.timeZone.IanaZoneName,
          );
          result.LastUpdatedDateTime = this.timeZoneService.formatDate(date);
        }
      }),
    );
  }

  getDocument(
    body: FolderManagementServiceOptions.GetDocument,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<FolderManagementServiceModel.Document>> {
    forceRequest = forceRequest === undefined ? true : !!forceRequest;
    return this.origamiPost('/GetDocument', body, null, silentErrorCodes, forceRequest).pipe(
      tap((res) => {
        if (res.Success) {
          const result: FolderManagementServiceModel.Document = res.Result;
          result.AuditHistory?.forEach((history) => {
            history.CreatedDateConverted = this.formatDate(history.CreatedDate);
          });
          result.QueryTickets?.forEach((queryTicket) => {
            queryTicket.CreatedDateString = this.formatDate(queryTicket.CreatedDate);
            queryTicket.LastUpdatedDateString = this.formatDate(queryTicket.LastUpdatedDate);
            queryTicket.QueryTicketActivities.forEach((activity) => {
              activity.CreatedDateString = this.formatDate(activity.CreatedDate);
            });
          });
          if (result.PostedDeviceInfo?.VerificationDate) {
            result.PostedDeviceInfo.VerificationDate = this.formatDate(result.PostedDeviceInfo?.VerificationDate);
          }
        }
      }),
    );
  }

  private formatDate(dateString: string): string {
    if (!dateString) {
      return null;
    }
    const date = Converter.DateAndTime.fromCSharpString(dateString);
    return this.timeZoneService.formatDate(date, DateFormatStore.luxonDateTimeFormatWithTime);
  }

  getWorkingDocumentId(
    body: FolderManagementServiceOptions.GetWorkingDocumentId,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<number>> {
    forceRequest = forceRequest === undefined ? true : !!forceRequest;
    return this.origamiPost('/GetWorkingDocumentId', body, null, silentErrorCodes, forceRequest);
  }

  getDocumentCollision(
    body: FolderManagementServiceOptions.GetDocumentCollision,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<FolderManagementServiceModel.DocumentCollisionInfo>> {
    return this.origamiPost('/GetDocumentCollision', body, null, silentErrorCodes, true);
  }

  updateNeedRedaction(body: FolderManagementServiceOptions.UpdateNeedRedaction, silentErrorCodes?: ErrorCode[]): any {
    return this.origamiPost('/UpdateNeedRedaction', body, null, silentErrorCodes, true);
  }

  getFolderProfile(
    body: FolderManagementServiceOptions.GetFolderProfile,
    silentErrorCodes?: ErrorCode[],
    forceRequest = true,
  ): Observable<OrigamiResponse<FolderManagementServiceModel.FolderProfile>> {
    return this.origamiPost('/GetFolderProfile', body, null, silentErrorCodes, forceRequest);
  }

  getFolderIndexTree(
    body: FolderManagementServiceOptions.GetFolderIndexTreeFields,
    force = true,
  ): Observable<OrigamiResponse<FolderManagementServiceModel.DocumentView[]>> {
    return this.origamiPost('/GetFolderIndexTree', body, null, null, force);
  }

  deleteDocument(params: FolderManagementServiceOptions.DeleteDocumentParams) {
    return this.origamiPost('/DeleteDocument', params, null, null, true);
  }

  enqueueFile(
    body: FolderManagementServiceOptions.EnqueueFileParams,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/EnqueueFile', body, null, silentErrorCodes, true);
  }

  getDocumentByView(
    body: FolderManagementServiceOptions.GetDocumentsByViewParams,
    silentErrorCodes: ErrorCode[] = null,
  ): Observable<OrigamiResponse<FolderManagementServiceModel.GetDocumentByViewResult>> {
    if (body && typeof body.fieldListing === 'object') {
      body.fieldListing = body.fieldListing.join(',');
    }

    ///
    // TODO temporary turned off search in index
    body.searchByFieldsInIndex = false;
    ///

    if (
      body.parentId === 0 &&
      !body.expand &&
      body.viewMode === FolderManagementServiceOptions.FolderTreeViewModes.BY_INDEX
    ) {
      // Don't send request for Index root folder if toggle is not expanded
      return of({ ErrorCode: 0, Result: { PageNumber: 0, TopicList: [], TotalRowCount: 0 } });
    }

    const auditViewMods = [
      FolderManagementServiceOptions.FolderTreeViewModes.MY_AUDIT_PENDING,
      FolderManagementServiceOptions.FolderTreeViewModes.MY_AUDIT_AUDITED,
      FolderManagementServiceOptions.FolderTreeViewModes.BY_AUDITOR_PENDING,
      FolderManagementServiceOptions.FolderTreeViewModes.MY_AUDIT_IN_PROGRESS,
      FolderManagementServiceOptions.FolderTreeViewModes.MY_AUDIT_RESOLVED,
      FolderManagementServiceOptions.FolderTreeViewModes.BY_AUDITOR_AUDITED,
      FolderManagementServiceOptions.FolderTreeViewModes.BY_AUDITOR_IN_PROGRESS,
      FolderManagementServiceOptions.FolderTreeViewModes.BY_AUDITOR_RESOLVED,
      FolderManagementServiceOptions.FolderTreeViewModes.BY_AUDITOR_PENDING,
    ];

    const newSearch = (typeof body.search === 'string' && body.search?.split('|')) || [];
    if (!body.expand && auditViewMods.includes(body.viewMode) && newSearch[newSearch.length - 1] === '0') {
      return of({ ErrorCode: 0, Result: { PageNumber: 0, TopicList: [], TotalRowCount: 0 } });
    }

    if (
      body.skipRequest &&
      (body.viewMode === FolderManagementServiceOptions.FolderTreeViewModes.EVENT_DOCUMENTS ||
        body.viewMode === FolderManagementServiceOptions.FolderTreeViewModes.ETMF_COMPLETENESS)
    ) {
      // Don't send request for view by Event & by eTMF Completeness if toggle is not expanded
      return of({ ErrorCode: 0, Result: { PageNumber: 0, TopicList: [], TotalRowCount: 0 } });
    }

    return this.origamiPost('/GetDocumentByView', body, null, silentErrorCodes, true).pipe(
      tap((res) => {
        if (res.Success) {
          const result: FolderManagementServiceModel.GetDocumentByViewResult = res.Result;
          result.TopicList?.forEach((topic) => {
            topic.ESignatureStatuses?.forEach((status) => {
              if (status.SignatureDate) {
                const date = Converter.DateAndTime.fromCSharpString(status.SignatureDate);
                const formattedDate = this.timeZoneService.formatDate(
                  date,
                  DateFormatStore.luxonDateTimeFormat + ' h:mm:ss a',
                  true,
                );
                status.SignatureDate = formattedDate;
              }
            });
          });
        }
        window.sessionStorage.setItem(
          'exportTopicsParamsBuffer',
          JSON.stringify({ requestParams: body, responseParams: res }),
        );
      }),
    );
  }

  cloneDocumentsToAutoFolders(body): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/CloneDocumentsToAutoFolders', body, null, null, true);
  }

  moveDocumentsBetweenModules(
    body: FolderManagementServiceOptions.MoveDocumentsBetweenModulesOpt,
    silentErrorCodes: ErrorCode[] = null,
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/MoveDocumentsBetweenModules', body, null, silentErrorCodes, true);
  }

  copyDocumentsToDatarooms(body: FolderManagementServiceOptions.ICopyDocuments): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/CopyDocumentsToDatarooms', body, null, null, true);
  }

  modifyIndexAndUnlock(body: FolderManagementServiceOptions.ModifyIndexAndUnlock): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/ModifyIndexAndUnlock', body, null, null, true);
  }

  createDocument(
    body: FolderManagementServiceOptions.CreateDocument,
    silentErrorCodes?: ErrorCode[],
    additionalRequestOptions?: AdditionalRequestOptions,
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/CreateDocument', body, null, silentErrorCodes, true, additionalRequestOptions);
  }

  editDocument(
    body: FolderManagementServiceOptions.EditDocument,
    silentErrorCodes?: ErrorCode[],
    additionalRequestOptions?: AdditionalRequestOptions,
  ): Observable<OrigamiResponse<FolderManagementServiceModel.ReplaceAttachmentURL>> {
    return this.origamiPost('/EditDocument', body, null, silentErrorCodes, true, additionalRequestOptions);
  }

  getDocumentVersions(
    body: FolderManagementServiceOptions.GetDocumentVersions,
  ): Observable<OrigamiResponse<FolderManagementServiceModel.DocumentVersion[]>> {
    return this.origamiPost('/GetDocumentVersions', body, null, null, true);
  }

  copyDocument(
    body: FolderManagementServiceOptions.CopyDocument,
    silentErrorCodes: ErrorCode[],
  ): Observable<OrigamiResponse<FolderManagementServiceModel.CopyDocument[]>> {
    return this.origamiPost('/CopyDocument', body, null, silentErrorCodes, true);
  }

  restoreDocuments(siteId: number, documents: { TopicId: number; TopicName: string; ParentId?: string }[]) {
    // may parentId is number?
    const body = { siteId, documents };
    return this.origamiPost('/RestoreDocuments', body, null, null, true);
  }

  purgeDocument(body) {
    return this.origamiPost('/PurgeDocument', body, null, null, true);
  }

  bulkImportDocumentsFromMetadata(body: FolderManagementServiceOptions.BulkImportItemsFromMetadata) {
    return this.origamiPost('/BulkImportDocumentsFromMetadata', body, null, null, true);
  }

  getDocumentOnlyOfficeInfo(
    body: FolderManagementServiceOptions.GetDocumentOnlyOfficeInfo,
  ): Observable<OrigamiResponse<FolderManagementServiceModel.OnlyOfficeInfo>> {
    const silentErrorCodes = [ErrorCode.DOCUMENT_IS_EDITED_ONLINE_BY_ANOTHER_USER];

    return this.origamiPost('/GetDocumentOnlyOfficeInfo', body, null, silentErrorCodes, true);
  }

  refreshAlfrescoTicket(url: string): Observable<any> {
    return this.outerGet(url);
  }

  getDocumentSharepointInfo(
    body: FolderManagementServiceOptions.GetDocumentSharepointInfo,
  ): Observable<OrigamiResponse<FolderManagementServiceModel.SharepointInfo>> {
    const silentErrorCodes = [ErrorCode.DOCUMENT_IS_EDITED_ONLINE_BY_ANOTHER_USER];

    return this.origamiPost('/GetDocumentSharepointInfo', body, null, silentErrorCodes, true);
  }

  getDocumentsByDocumentTypeFilter(body: FolderManagementServiceOptions.GetDocumentsByDocumentTypeFilter) {
    return this.origamiPost('/GetDocumentsByDocumentTypeFilter', body, null, null, true);
  }

  revertDocument(body: FolderManagementServiceOptions.RevertDocument): Observable<OrigamiResponse<boolean>> {
    return this.origamiPost('/RevertDocument', body, null, null, true);
  }

  mergeDocuments(body: FolderManagementServiceOptions.MergeDocuments): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/MergeDocuments', body, null, null, true);
  }

  makeDocumentsObsolete(body: FolderManagementServiceOptions.MakeDocumentsObsolete): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/MakeDocumentsObsolete', body, null, null, true);
  }

  saveIgnoredSuggestedDocumentTypes(
    body: FolderManagementServiceOptions.SaveIgnoredSuggestedDocumentTypes,
  ): Observable<OrigamiResponse<boolean>> {
    return this.origamiPost('/SaveIgnoredSuggestedDocumentTypes', body, null, null, true);
  }

  getDocumentSubmissionInfo(
    body: FolderManagementServiceOptions.GetDocumentSubmissionInfo,
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/GetDocumentSubmissionInfo', body, null, null, true);
  }

  setDuplicateStatus(body: FolderManagementServiceOptions.SetDuplicateStatus): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/SetDuplicateStatus', body, null, null, true);
  }

  setDocumentStartingVersion(
    body: FolderManagementServiceOptions.SetDocumentStartingVersionParams,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/SetDocumentStartingVersion', body, null, silentErrorCodes, true);
  }

  getDocumentDistributionLog(
    body: FolderManagementServiceOptions.GetDocumentDistributionLogParams,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<FolderManagementServiceModel.DocumentDistributionLog[]>> {
    return this.origamiPost('/GetDocumentDistributionLog', body, null, silentErrorCodes, true);
  }

  sendDocumentsToTraining(
    body: FolderManagementServiceOptions.SendDocumentsToTrainingParams,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/SendDocumentsToTraining', body, null, silentErrorCodes, true);
  }

  rejectDocuments(
    body: FolderManagementServiceOptions.RejectDocumentsParams,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/RejectDocuments', body, null, silentErrorCodes, true);
  }

  getTrainingStatus(
    body: FolderManagementServiceOptions.SendDocumentsToTrainingParams,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<FolderManagementServiceModel.GetTrainingStatus[]>> {
    return this.origamiPost('/GetTrainingStatus', body, null, silentErrorCodes, true);
  }

  /*
  getDocumentConfidenceLevelScore(
    body: FolderManagementServiceOptions.GetDocumentConfidenceLevelScoreParams,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<FolderManagementServiceModel.DocumentConfidenceLevelScore>> {
    return this.origamiPost('/GetDocumentConfidenceLevelScore', body, null, silentErrorCodes, true);
  }
  */

  getTIAutomateEntries(
    body: FolderManagementServiceOptions.GetTIAutomateEntriesParams,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<TIAutomateDocumentData>> {
    return this.origamiPost('/GetTIAutomateEntries', body, null, silentErrorCodes, true);
  }

  getTIAutomateTaskData(
    body: FolderManagementServiceOptions.GetTiAutomateTaskDataParams,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/GetTIAutomateTaskData', body, null, silentErrorCodes, true);
  }

  submitTIAutomateTask(
    body: FolderManagementServiceOptions.SubmitTIAutomateTaskParams,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/SubmitTIAutomateTask', body, null, silentErrorCodes, true);
  }

  setAiFlagStatus(
    body: FolderManagementServiceOptions.SetAiFlagStatusParams,
  ): Observable<OrigamiResponse<FolderManagementServiceModel.AiFlagActionResult>> {
    return this.origamiPost('/SetAiFlagStatus', body, null, null, true);
  }

  getAiFlagDetails(
    body: FolderManagementServiceOptions.GetAiFlagDetailsParams,
  ): Observable<OrigamiResponse<FolderManagementServiceModel.AiFlagDetail[]>> {
    return this.origamiPost('/GetAiFlagDetails', body, null, null, true);
  }
}
